import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { Link } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { useTrackingTrigger, TrackingTrigger } from '@indeed/flex-tracking-context'

import Acp from 'syft-acp-uikit'
import { entityList$, pagination$ } from 'syft-acp-core/reducers/generators/entities'
import { fetchWorkerBlocks } from 'syft-acp-core/store/worker-blocks/actions'
import type { WorkerBlockEntity } from 'syft-acp-core/store/worker-blocks/types'
import { loginAsEmployer } from 'syft-acp-core/store/employers/actions'
import { setEmployerLoading, saveEmployerToken } from 'syft-acp-core/actions/employer-tokens'
import { createUrl } from 'syft-acp-core/entities/EmployerDetail/helpers'
import type { TokenResponse } from 'syft-acp-core/entities/EmployerDetail/types'
import { loginAsEmployerBlacklistedIds } from 'syft-acp-core/entities/EmployerDetail/constants'

import { trackingEvents } from './WorkerBlocksList.tracking'
import { blockedByLabel, expiresLabel, levelLabel } from './helpers'
import type { WorkerBlocksListProps } from './WorkerBlocksList.types'

type BlockRowButton = {
  rowData: WorkerBlockEntity
}

/**
 * Read-only worker Blocks tab. Acp.EntityList is the current table abstraction
 * (ADR-0006); Edit impersonates into the employer client-portal blocked-workers
 * list because ACP itself does not mutate blocks.
 */
export const WorkerBlocksList = ({ workerID }: WorkerBlocksListProps) => {
  const dispatch = useDispatch()
  const trackingTrigger = useTrackingTrigger()
  const tokenActions = useMemo(
    () => bindActionCreators({ loginAsEmployer, setEmployerLoading, saveEmployerToken }, dispatch),
    [dispatch],
  )

  const { data, meta, isLoading, userId, query } = useSelector(state => {
    const nextQuery = state.routing.locationBeforeTransitions.query
    const listQuery = { workerID, ...nextQuery }

    return {
      data: entityList$(state.workerBlocks, listQuery) as WorkerBlockEntity[],
      meta: pagination$(state.workerBlocks, listQuery),
      isLoading: state.workerBlocks.isLoadingData,
      userId: state.auth?.userData?.id,
      query: nextQuery,
    }
  }, shallowEqual)

  useEffect(() => {
    if (workerID) dispatch(fetchWorkerBlocks({ workerID, ...query }))
  }, [dispatch, workerID, query])

  const handleEdit = ({ rowData }: BlockRowButton) => {
    const employerId = rowData.employer_id
    if (!employerId || loginAsEmployerBlacklistedIds.includes(employerId)) return
    trackingTrigger(trackingEvents.EDIT.CLICKED, { employer_id: employerId })
    tokenActions.setEmployerLoading({ id: employerId })
    void new Promise<TokenResponse>(resolve => tokenActions.loginAsEmployer(employerId, resolve)).then(res =>
      createUrl({
        res,
        userID: Number(userId),
        actions: tokenActions,
        id: employerId,
        listingId: undefined,
        shiftID: undefined,
        workerID: undefined,
        blockedWorkers: true,
        target: '_blank',
      }),
    )
  }

  return (
    <>
      <Acp.EntityList
        hasResultCount
        hasPagination
        inContainer
        idKeyValue="id"
        data={{ data, meta }}
        isLoading={isLoading}
      >
        <Acp.Table>
          <Acp.Col.Text
            value={(row: WorkerBlockEntity) => (
              <Link to={`/entity/employers/view/${row.employer_id}`}>
                {row.employer_name || `Employer #${row.employer_id}`}
              </Link>
            )}
            header="Employer"
          />
          <Acp.Col.Text value={(row: WorkerBlockEntity) => levelLabel(row.level)} header="Level" isMinimal />
          <Acp.Col.Text value="work_location_name" header="Venue" />
          <Acp.Col.Timestamp value="blocked_at" header="Blocked at" isMinimal />
          <Acp.Col.Text value={blockedByLabel} header="Blocked by" />
          <Acp.Col.Text value={expiresLabel} header="Expires" isMinimal />
          <Acp.Col.Button isMinimal header="Edit">
            <Acp.Button onClick={handleEdit}>Edit</Acp.Button>
          </Acp.Col.Button>
        </Acp.Table>
      </Acp.EntityList>
      <TrackingTrigger onLoad={{ event: trackingEvents.PAGE.VIEWED, payload: { worker_id: workerID } }} />
    </>
  )
}
